import React, { useEffect, useState } from 'react';
import { Button } from 'rsuite';

// A one-at-a-time review carousel with a stable, cursor-based order. The card
// shown is items[index]; deciding calls onDecide (the parent persists and
// advances the cursor), and "Back" calls onBack (parent moves the cursor back
// one). The list itself never reorders, so Back behaves exactly like a browser
// back button and a revisited card shows its previously chosen action.
//
// Props:
//   items:      array of objects with a unique `reviewKey`
//   index:      cursor into items (current card = items[index])
//   decisions:  { [reviewKey]: value } — highlights the chosen action on revisit
//   actions:    [{ label, value, color, appearance, icon, hotkey, direction }]
//   onDecide:   async (item, value) => void  (parent persists + advances cursor)
//   onBack:     () => void | null            (parent moves the cursor back one)
//   canBack:    boolean
//   onSkip:     () => void | null            (skip the rest of the queue)
//   renderCard: (item) => JSX (card body, above the action buttons)
//   hint:       JSX/text shown between Back and Skip
//   disabled:   boolean
//   total:      total item count for the "N of M" footer
export default function ReviewCarousel({
  items, index, decisions, actions, onDecide, onBack, canBack, onSkip, renderCard, hint, disabled, total,
}) {
  const [exiting, setExiting] = useState(null); // { reviewKey, direction }
  const current = items[index] || null;
  const backEnabled = canBack !== undefined ? canBack : index > 0;
  const chosen = current && decisions ? decisions[current.reviewKey] : undefined;

  const isEditableTarget = (target) => {
    if (!target || !(target instanceof Element)) return false;
    if (target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""], [contenteditable]')) return true;
    const role = target.getAttribute?.('role');
    return role === 'textbox' || role === 'combobox' || target.getAttribute?.('aria-multiline') === 'true';
  };

  const act = (item, action) => {
    if (!item || exiting || disabled) return;
    setExiting({ reviewKey: item.reviewKey, direction: action.direction || 'left' });
    window.setTimeout(async () => {
      await onDecide(item, action.value);
      setExiting(null);
    }, 280);
  };

  const back = () => {
    if (!backEnabled || exiting || disabled || !onBack) return;
    onBack();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (disabled || exiting) return;
      if (isEditableTarget(e.target)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'backspace' && onBack) { e.preventDefault(); back(); return; }
      const cur = items[index];
      if (!cur) return;
      const action = actions.find((a) => (a.hotkey || '').toLowerCase() === k);
      if (action) { e.preventDefault(); act(cur, action); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, index, exiting, disabled, actions, onBack, backEnabled]);

  if (!current) return null;

  const isExiting = exiting && exiting.reviewKey === current.reviewKey;
  const dir = exiting?.direction === 'right' ? 1 : -1;

  return (
    <>
      <style>{'@keyframes rndCardIn{from{opacity:0;transform:translateX(48px)}to{opacity:1;transform:translateX(0)}}'}</style>
      <div className="d-flex align-items-center mb-3 gap-2">
        {onBack ? (
          <Button size="sm" appearance="subtle" onClick={back} disabled={!backEnabled || !!exiting}>
            <i className="bi bi-arrow-left me-1" /> Back
          </Button>
        ) : <span />}
        <div className="text-muted small text-center flex-grow-1">{hint}</div>
        {onSkip ? (
          <Button size="sm" appearance="ghost" onClick={onSkip} disabled={!!exiting}>
            Skip remaining <i className="bi bi-chevron-double-right ms-1" />
          </Button>
        ) : <span />}
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', minHeight: 340, position: 'relative' }}>
        <div
          key={current.reviewKey}
          style={{
            transition: 'transform 280ms ease, opacity 280ms ease',
            transform: isExiting ? `translateX(${dir * 135}%) rotate(${dir * 6}deg)` : 'translateX(0) rotate(0)',
            opacity: isExiting ? 0 : 1,
            animation: isExiting ? 'none' : 'rndCardIn 260ms ease',
          }}
        >
          <div style={{ border: '1px solid var(--dm-border, #e6e6e6)', borderRadius: 14, boxShadow: '0 10px 28px rgba(0,0,0,0.09)', background: 'var(--dm-surface, #fff)', padding: 20 }}>
            {renderCard(current)}
            {chosen !== undefined && chosen !== null ? (
              <div className="text-center text-muted small mt-3">
                <i className="bi bi-info-circle me-1" />
                Previously set to <strong>{actions.find((a) => a.value === chosen)?.label || chosen}</strong> — pick again to change.
              </div>
            ) : null}
            <div className="d-flex gap-2 mt-4 justify-content-center flex-wrap">
              {actions.map((a) => {
                const isChosen = chosen !== undefined && chosen !== null && a.value === chosen;
                return (
                  <Button
                    key={a.value}
                    size="lg"
                    color={a.color}
                    appearance={isChosen ? 'primary' : (a.appearance || 'default')}
                    style={{ minWidth: 140, outline: isChosen ? '2px solid var(--dm-border, rgba(0,0,0,0.15))' : 'none', outlineOffset: 2 }}
                    onClick={() => act(current, a)}
                    disabled={!!exiting}
                  >
                    {a.icon ? <i className={`bi ${a.icon} me-1`} /> : null}
                    {a.label}
                    {isChosen ? <i className="bi bi-check2 ms-1" /> : null}
                    {a.hotkey ? <kbd className="ms-1">{a.hotkey.toUpperCase()}</kbd> : null}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="text-muted small text-center mt-3">
        {Math.min(index + 1, total || items.length)} of {total || items.length}
      </div>
    </>
  );
}
